export type PlatformRole = "platform_admin" | "platform_operator" | "platform_support" | "none";

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  emailVerified?: boolean;
  platformRole?: PlatformRole;
}

export interface IngestResult {
  contractId: string;
  documentId: string;
  title: string;
  counterparty: string;
  type: string;
  overallRisk: string;
  counts: { terms: number; clauses: number; obligations: number; reviewQueue: number };
  model: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  role: string;
  permissions: string[];
}

export interface Product {
  id: string;
  key: string;
  name: string;
  description: string;
  status: string;
  comingSoon: boolean;
  entitlement: string;
}

export interface Price {
  id: string;
  interval: string;
  currency: string;
  unitAmount: number;
  perSeat: boolean;
}

export interface CatalogItem {
  sku: string;
  name: string;
  description: string;
  kind: "app" | "bundle" | "addon" | "package";
  entitlements: string[];
  prices: {
    month: { unitAmount: number; currency: string };
    year: { unitAmount: number; currency: string };
  };
  /** False while the SKU is still advertised but cannot be provisioned. */
  available: boolean;
}

// ---- Studio Marketplace (ODS-CATALOG-001) --------------------------------

export type CatalogLifecycleStatus =
  | "draft"
  | "internal"
  | "preview"
  | "beta"
  | "available"
  | "maintenance"
  | "suspended"
  | "deprecated"
  | "retired"
  | "coming_soon";

export type CatalogCustomerStateValue =
  | "not_entitled"
  | "trial_available"
  | "trialing"
  | "subscribed"
  | "suspended"
  | "expired"
  | "upgrade_available"
  | "contact_sales"
  | "unavailable"
  | "coming_soon"
  | "retired";

export interface CatalogPriceRef {
  unitAmount: number;
  currency: string;
}

/** How an offering reaches the customer; only `platform` is provisioned by Studio. */
export type CatalogDelivery = "platform" | "external" | "service";

export interface CatalogApplication {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  description: string;
  icon: string;
  lifecycleStatus: CatalogLifecycleStatus;
  primaryEntitlement: string;
  category: { key: string; name: string } | null;
  route: string | null;
  delivery: CatalogDelivery;
  /** Where a non-platform offering sends the customer (relative or absolute). */
  externalUrl: string | null;
  prices: { month?: CatalogPriceRef; year?: CatalogPriceRef };
}

export interface CatalogApplicationDetail extends CatalogApplication {
  /** Present only when this app can be trialed self-service, so signup can offer it by name. */
  trial: { enabled: true; days: number } | null;
  maintenanceMessage: string | null;
  deprecationNotice: string | null;
  entitlements: { key: string; required: boolean; label: string }[];
  trust: {
    trustCenterUrl: string | null;
    dataHandling: string;
    certifications: string[];
    securityNotes: string;
  } | null;
  release: {
    currentVersion: string;
    releaseNotesUrl: string | null;
    releasedAt: string | null;
    knownLimitations: string;
    changelog: unknown[];
  } | null;
  versions: { version: string; isCurrent: boolean; notes: string; releasedAt: string }[];
  bundles: { slug: string; name: string }[];
}

export interface CatalogCategory {
  key: string;
  name: string;
  description: string;
}

export interface CatalogBundle {
  id: string;
  slug: string;
  name: string;
  description: string;
  kind: string;
  lifecycleStatus: CatalogLifecycleStatus;
  /** Bundle membership is advertising: a member may still be `coming_soon`. */
  applications: { slug: string; name: string; lifecycleStatus: CatalogLifecycleStatus }[];
  prices: Record<string, CatalogPriceRef>;
}

export interface CatalogCustomerState {
  slug: string;
  name: string;
  lifecycleStatus: CatalogLifecycleStatus;
  route: string | null;
  state: CatalogCustomerStateValue;
  canLaunch: boolean;
  canTrial: boolean;
  canSubscribe: boolean;
  upgradeAvailable: boolean;
  trialEndsAt: string | null;
  notice: string | null;
}

// Per (org, user) preference over a catalog app: pinned + recently-used.
export interface CatalogPreference {
  slug: string;
  pinned: boolean;
  lastOpenedAt: string | null;
  openCount: number;
}

// ---- Catalog administration (ODS-CATALOG-001 P4) -------------------------

export interface AdminApplicationSummary {
  id: string;
  slug: string;
  name: string;
  icon: string;
  lifecycleStatus: CatalogLifecycleStatus;
  sortOrder: number;
  planKey: string | null;
  primaryEntitlement: string;
  publishedAt: string | null;
  categoryName: string | null;
}

export interface AdminEntitlementMapping {
  key: string;
  required: boolean;
  label: string;
}

export interface AdminPriceMapping {
  id?: string;
  priceId: string;
  interval: string;
}

export interface AdminApplicationDetail {
  application: {
    id: string;
    slug: string;
    name: string;
    tagline: string;
    description: string;
    icon: string;
    categoryId: string | null;
    lifecycleStatus: CatalogLifecycleStatus;
    planKey: string | null;
    primaryEntitlement: string;
    maintenanceMessage: string | null;
    deprecationNotice: string | null;
    sortOrder: number;
    publishedAt: string | null;
  };
  entitlements: AdminEntitlementMapping[];
  prices: AdminPriceMapping[];
  trialPolicy: { enabled: boolean; trialDays: number; planKey: string | null } | null;
  navigation: { route: string | null; navLabel: string; navSection: string; sortOrder: number } | null;
  trust: { trustCenterUrl: string | null; dataHandling: string; certifications: string[]; securityNotes: string } | null;
  release: { currentVersion: string; releaseNotesUrl: string | null; releasedAt: string | null; knownLimitations: string; changelog: unknown[] } | null;
  versions: { id: string; version: string; isCurrent: boolean; notes: string; releasedAt: string }[];
  bundles: { slug: string; name: string }[];
}

export interface AdminBundleSummary {
  id: string;
  slug: string;
  name: string;
  kind: string;
  lifecycleStatus: CatalogLifecycleStatus;
  primaryEntitlement: string;
  sortOrder: number;
}

export interface AdminBundleDetail {
  bundle: {
    id: string;
    slug: string;
    name: string;
    description: string;
    kind: string;
    planKey: string | null;
    primaryEntitlement: string;
    lifecycleStatus: CatalogLifecycleStatus;
    sortOrder: number;
  };
  applications: { slug: string; name: string }[];
  prices: AdminPriceMapping[];
}

export interface AdminReference {
  categories: { id: string; key: string; name: string }[];
  plans: { id: string; productKey: string; name: string; entitlements: string[] }[];
  prices: { id: string; planId: string; interval: string; unitAmount: number; currency: string }[];
  entitlementKeys: string[];
}

export interface AdminAuditEvent {
  id: string;
  action: string;
  target: string | null;
  actorUserId: string | null;
  actorEmail: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface Plan {
  id: string;
  productKey: string;
  name: string;
  tier: string;
  contactSales: boolean;
  includedUsage: number;
  features: string[];
  entitlements: string[];
  prices: Price[];
}

export interface Subscription {
  id: string;
  organization_id: string;
  plan_id: string;
  price_id: string;
  status: string;
  seats: number;
  assigned_seats: number;
  current_period_start: string;
  current_period_end: string;
  trial_ends_at: string | null;
  cancel_at_period_end: number;
  plan_name: string;
  plan_tier: string;
}

export interface Invoice {
  id: string;
  number: string;
  status: string;
  currency: string;
  subtotal: number;
  tax: number;
  total: number;
  due_at: string | null;
  paid_at: string | null;
  created_at: string;
}

export interface Entitlement {
  key: string;
  status: string;
  source: string;
  expires_at: string | null;
}

export interface UsageSummary {
  capabilityKey: string;
  totalQuantity: number;
  recordCount: number;
}

export interface DashboardMetrics {
  totalContracts: number;
  reviewQueue: number;
  upcomingRenewals: number;
  overdueObligations: number;
  highRiskClauses: number;
  verificationBacklog: number;
  contractValue: number;
  extractionInProgress: number;
  roiSummary: { hoursSaved: number; dollarsSaved: number };
}

export interface ContractSummary {
  id: string;
  title: string;
  counterparty: string;
  type: string;
  status: string;
  valueAmount: number | null;
  currency: string;
  renewalDate: string | null;
  riskLevel: string;
  verification: string;
  extractionStatus: string;
  tags: string[];
}

export interface Citation {
  id: string;
  locator: string;
  snippet: string;
}

export interface Term {
  id: string;
  label: string;
  value: string;
  confidence: number;
  citationId: string | null;
  verification: string;
}

export interface Clause {
  id: string;
  clauseType: string;
  riskLevel: string;
  text: string;
  confidence: number;
  citationId: string | null;
  verification: string;
  playbookFinding: string | null;
}

export interface Obligation {
  id: string;
  contractId: string;
  contractTitle?: string;
  description: string;
  owner: string | null;
  dueDate: string | null;
  status: string;
  calendarLinked: number;
}

export interface ContractDetail {
  contract: ContractSummary & { summary: string | null; createdAt: string };
  citations: Citation[];
  terms: Term[];
  clauses: Clause[];
  obligations: Obligation[];
}

export interface ReviewItem {
  id: string;
  contractId: string;
  contractTitle: string;
  fieldLabel: string;
  extractedValue: string;
  confidence: number;
  status: string;
  citationLocator: string | null;
  citationSnippet: string | null;
}

export interface QaCitation {
  contractId: string;
  contractTitle: string;
  locator: string;
  snippet: string;
}

export interface QaResult {
  id: string;
  grounded: boolean;
  answer: string | null;
  message?: string;
  confidence?: number;
  citations: QaCitation[];
}

export interface CompliancePack {
  id: string;
  framework: string;
  readiness: number;
  evidenceCount: number;
  status: string;
}

export interface AppSumoTierSummary {
  redeemedCodes: number;
  tierCodes: number;
  maxCodes: number;
  seats: number | null;
  monthlyPages: number | null;
  entitlements: string[];
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /**
     * Structured detail the API attached to the error — for example the
     * entitlement or permission key a denial names. Callers should prefer this
     * over parsing the message.
     */
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}
